'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Text } from '@tremor/react';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { format } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
} from '@/components/molecules/Table';
import type { ITableData, ITableDataWithExpansion, IColumnDef, ITableWidget } from './types';
import { getColumnType, formatValueByType, parseValue, filterRowsWithAllZeroMetrics, truncateText, hasNumericMetrics } from './utils';
import { getColumnTypeInfo, calculateColumnAverage, calculateColumnSum } from './utils/tableCalculations';
import { useWidgetFiltersController } from '../WidgetFilters/useWidgetFiltersController';
import { useWidgetProducts } from '../WidgetFilters/useWidgetProducts';

import { StarRating } from '@/components/atoms/StarRating';
import { CircularProgress } from '@/components/atoms/CircularProgress';
import { URLCell } from '@/components/atoms/URLCell';
import { ColumnWrapper } from './ColumnWrapper';
import { SoldBadgeWrapper } from './ColumnWrapper/SoldBadgeWrapper';
import { NoDataView, FilteredNoDataView } from '../NoDataView';
import { ActivityIcon } from './ActivityIcon';
import { ConversionPathCell } from './ConversionPathCell';
import { SERPFeaturesCell } from './SERPFeaturesCell';
import { AioPagesCell } from './AioPagesCell';

interface TremorCompareTableProps {
  data: ITableData | ITableDataWithExpansion;
  compare?: ITableWidget['compare'];
  widgetFiltersController?: ReturnType<typeof useWidgetFiltersController>;
  filterDimension?: string;

  productLevel?: boolean;
  widget?: any;
  currentDateRange?: { from: Date; to: Date } | null;
  compareDateRange?: { from: Date; to: Date } | null;
}

function hasExpandableRows(data: ITableData | ITableDataWithExpansion): data is ITableDataWithExpansion {
  if (!data || !data.rows || !Array.isArray(data.rows) || data.rows.length === 0) {
    return false;
  }

  const hasExpansion = data.rows.some((row) => 'expansion' in row && (row as any).expansion !== undefined);
  const hasExpandableMetadata = !!(data.metadata && 'expandable' in data.metadata && (data as any).metadata.expandable?.enabled);

  return !!(hasExpansion || hasExpandableMetadata);
}

export function TremorCompareTable({ data: _data, compare, widgetFiltersController, filterDimension, productLevel, widget, currentDateRange, compareDateRange }: TremorCompareTableProps) {
  // Use widget metadata for default sorting if available, otherwise fall back to data-level sorting
  const defaultSortField = widget?.metadata?.sortBy || _data?.sortBy;
  const defaultSortDirection = widget?.metadata?.sortDirection || _data?.sortDirection || 'asc';
  
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultSortDirection);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [displayedRowsCount, setDisplayedRowsCount] = useState(15);
  const [compareExpanded, setCompareExpanded] = useState<Record<string, boolean>>({});
  const rowsPerPage = 15;

  const lastAutoAppliedSelectionKeyRef = useRef<string | null>(null);
  const lastAutoAppliedRowsKeyRef = useRef<string | null>(null);
  const prevSelectionKeyRef = useRef<string | null>(null);
  const userInteractedWithExpansionRef = useRef<boolean>(false);

  const tableRef = useRef<HTMLTableElement>(null);

  // Total products available for filtering on this widget; used to gate single-product auto-expansion
  const availableProducts = useWidgetProducts(widget);

  const stableIdentifierKey = (identifiers?: Record<string, any>) => {
    if (!identifiers) return '';
    const entries = Object.entries(identifiers).sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(entries);
  };

  // Format date ranges for display
  const currentLabel = useMemo(() => {
    if (currentDateRange?.from && currentDateRange?.to) {
      return (
        <>
          {format(currentDateRange.from, 'M/d/yyyy')} –<br />
          {format(currentDateRange.to, 'M/d/yyyy')}
        </>
      );
    }
    return 'Current';
  }, [currentDateRange]);

  const compareLabel = useMemo(() => {
    if (compareDateRange?.from && compareDateRange?.to) {
      return (
        <>
          {format(compareDateRange.from, 'M/d/yyyy')} –<br />
          {format(compareDateRange.to, 'M/d/yyyy')}
        </>
      );
    }
    return 'Previous';
  }, [compareDateRange]);

  const isExpandableInput = hasExpandableRows(_data);
  const shouldUseUnifiedCompare = !isExpandableInput;

  const data = useMemo(() => {
    type UnifiedCompareRow = {
      identifiers?: Record<string, any>;
      current?: Record<string, any>;
      previous?: Record<string, any>;
      expansion?: {
        columns?: {
          current?: IColumnDef[];
          previous?: IColumnDef[];
        };
        rows?: Array<{
          identifiers?: Record<string, any>;
          current?: Record<string, any>;
          previous?: Record<string, any>;
        }>;
        metadata?: {
          current?: {
            breakdownBy?: string;
            style?: {
              variant?: 'subdued';
            };
          };
          previous?: {
            breakdownBy?: string;
            style?: {
              variant?: 'subdued';
            };
          };
        };
      };
    };

    const unifiedRows = (compare?.unified?.rows as UnifiedCompareRow[] | undefined) ?? undefined;

    const baseRows: Record<string, any>[] = (() => {
      if (!unifiedRows || unifiedRows.length === 0) return _data?.rows ?? [];

      const getKeyFromIdentifiers = (identifiers?: Record<string, any>) => {
        const rowId = identifiers?.rowId;
        if (typeof rowId === 'string' && rowId.length > 0) return rowId;
        return stableIdentifierKey(identifiers);
      };

      // Non-expandable tables: build the table rows purely from unified compare payload
      if (shouldUseUnifiedCompare) {
        const merged = new Map<string, UnifiedCompareRow>();

        unifiedRows.forEach((item: UnifiedCompareRow) => {
          const key = getKeyFromIdentifiers(item.identifiers);
          const existing = merged.get(key) ?? {};
          merged.set(key, {
            identifiers: item.identifiers ?? existing.identifiers,
            current: item.current ?? existing.current,
            previous: item.previous ?? existing.previous,
          });
        });

        let i = 0;
        return Array.from(merged.values()).map((mergedItem) => {
          const identifiers = mergedItem.identifiers ?? {};
          const current = mergedItem.current ?? {};
          const previous = mergedItem.previous ?? {};

          return {
            ...current,
            ...identifiers,
            rowId: identifiers.rowId ?? `unified-${i++}`,
            __compare: { identifiers, current, previous },
          };
        });
      }

      // Expandable tables: keep base expandable shape, but overlay unified compare values by rowId
      const base = (_data?.rows ?? []) as Record<string, any>[];
      const unifiedByRowId = new Map<string, UnifiedCompareRow>();

      unifiedRows.forEach((item: UnifiedCompareRow) => {
        const key = getKeyFromIdentifiers(item.identifiers);
        if (key) unifiedByRowId.set(key, item);
      });

      const colKeys = (_data?.columns ?? []).map((c) => c.field || c.metric || '').filter(Boolean);

      return base.map((row: any) => {
        const rowId = row?.rowId;
        const compareRow = typeof rowId === 'string' ? unifiedByRowId.get(rowId) : undefined;

        const identifiers = compareRow?.identifiers ?? (typeof rowId === 'string' ? { rowId } : {});
        const current = compareRow?.current ?? colKeys.reduce<Record<string, any>>((acc, key) => {
          acc[key] = row?.[key];
          return acc;
        }, {});
        const previous = compareRow?.previous ?? {};

        const mergedRow: any = {
          ...row,
          ...current,
          ...identifiers,
          __compare: { identifiers, current, previous },
        };

        if (compareRow?.expansion) {
          const expColumns =
            compareRow.expansion.columns?.current ??
            compareRow.expansion.columns?.previous ??
            mergedRow.expansion?.columns ??
            [];

          const expMetadata = compareRow.expansion.metadata?.current ?? compareRow.expansion.metadata?.previous ?? mergedRow.expansion?.metadata;
          const expRows = (compareRow.expansion.rows ?? []).map((expItem, idx) => {
            const expIdentifiers = expItem.identifiers ?? {};
            const expCurrent = expItem.current ?? {};
            const expPrevious = expItem.previous ?? {};

            return {
              ...expCurrent,
              ...expIdentifiers,
              rowId: `${rowId}-exp-${idx}`,
              __compare: { identifiers: expIdentifiers, current: expCurrent, previous: expPrevious },
            };
          });

          mergedRow.expansion = {
            columns: expColumns,
            rows: expRows,
            metadata: expMetadata,
          };
        }

        return mergedRow;
      });
    })();

    let processedRows = baseRows;

    if (widgetFiltersController && processedRows && (widgetFiltersController.dimensionFilterActive || widgetFiltersController.selectedDimensionItems.length > 0)) {
      const filterKey = filterDimension ?? '';

      processedRows = processedRows.filter((row) => {
        const dimensionValue = filterKey ? row[filterKey] : undefined;
        return widgetFiltersController.selectedDimensionItems.some((item) => dimensionValue == item.text);
      });
    }

    // Filter out rows with all zero metrics (recurses into expansion)
    processedRows = filterRowsWithAllZeroMetrics(processedRows ?? [], _data?.columns ?? []);

    return {
      ..._data,
      rows: processedRows,
    };
  }, [_data, compare, widgetFiltersController?.selectedDimensionItems, widgetFiltersController?.dimensionFilterActive, filterDimension, shouldUseUnifiedCompare]);

  const maybeApplyAutoExpansionForProductSelection = () => {
    if (!widget?.filterByProduct) return;
    const selectedProducts = widgetFiltersController?.selectedProducts || [];
    if (!selectedProducts.length) return;

    const selectionKey = selectedProducts
      .map((p) => p.id)
      .slice()
      .sort()
      .join(',');

    if (prevSelectionKeyRef.current !== selectionKey) {
      userInteractedWithExpansionRef.current = false;
      prevSelectionKeyRef.current = selectionKey;
    }

    if (userInteractedWithExpansionRef.current) return;

    const rowsKey = (data.rows || [])
      .map((r: any) => String(r.rowId))
      .slice()
      .sort()
      .join('|');

    if (
      lastAutoAppliedSelectionKeyRef.current === selectionKey &&
      lastAutoAppliedRowsKeyRef.current === rowsKey
    ) {
      return;
    }

    // Only auto-expand when the user has narrowed to a single product out of several.
    // The initial product filter auto-selects ALL products; for a dealer with a single
    // product that resolves to length === 1, which must NOT be treated as a drill-down (LAD-445).
    if (selectedProducts.length === 1 && availableProducts.length > 1) {
      if (!data.rows?.length) return;
      const rows = data.rows
        .map((row) => {
          const allExpandedRows = row.expansion?.rows?.map((expRow: any) => `${row.rowId}-exp-${expRow.rowId}`) || [];
          return [row.rowId, ...allExpandedRows];
        })
        .flat();

      const parsedRows = rows.reduce(
        (acc, rowId) => {
          acc[rowId] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );

      setExpandedRows(parsedRows);
    } else if (selectedProducts.length > 1) {
      setExpandedRows({});
    }

    lastAutoAppliedSelectionKeyRef.current = selectionKey;
    lastAutoAppliedRowsKeyRef.current = rowsKey;
  };

  useEffect(() => {
    maybeApplyAutoExpansionForProductSelection();
  }, [widget?.filterByProduct, widgetFiltersController?.selectedProducts, data.rows, availableProducts.length]);

  if (!data) {
    console.error('🔍 [TremorCompareTable] ERROR: No data provided');
    return (
      <Card className="w-full p-4 text-center">
        <Text>No table data available</Text>
      </Card>
    );
  }

  if (!data.columns || !Array.isArray(data.columns) || data.columns.length === 0) {
    console.error('TremorCompareTable: Invalid or missing columns', data);
    return (
      <Card className="w-full p-4 text-center">
        <Text>Invalid table structure: missing columns</Text>
      </Card>
    );
  }

  if (!data.rows || !Array.isArray(data.rows)) {
    console.error('TremorCompareTable: Invalid or missing rows', data);
    return (
      <Card className="w-full p-4 text-center">
        <Text>Invalid table structure: missing rows</Text>
      </Card>
    );
  }

  const displayColumns = data.columns;
  const getColumnKey = (col: IColumnDef | undefined) => (col?.field || col?.metric || '');

  const getRowValue = (row: Record<string, any>, fieldName: string) => {
    let displayValue = row[fieldName];
    if (displayValue === undefined && fieldName) {
      try {
        const rowKeys = Object.keys(row);
        const matchingKey = rowKeys.find((key) => {
          if (!key || !fieldName) return false;
          return key.toLowerCase() === fieldName.toLowerCase();
        });

        if (matchingKey) {
          displayValue = row[matchingKey];
        }
      } catch (error) {
        console.error(`Error matching field: ${fieldName}`, error);
      }
    }

    // Product-level synthetic top grouping: show product label in the first column
    if (
      productLevel === true &&
      displayValue === undefined &&
      typeof (row as any)?.rowId === 'string' &&
      (row as any).rowId.startsWith('prod_')
    ) {
      const firstColumnKey = getColumnKey(displayColumns?.[0]);
      if (firstColumnKey && fieldName && firstColumnKey.toLowerCase() === fieldName.toLowerCase()) {
        displayValue = (row as any).productName ?? (row as any).sourceLabel ?? (row as any).product;
      }
    }

    return displayValue;
  };

  const getCompareValue = (row: Record<string, any>, period: 'current' | 'previous', fieldName: string) => {
    const compareObj = (row as any)?.__compare;
    if (!compareObj) {
      return getRowValue(row, fieldName);
    }

    const source = period === 'current' ? compareObj.current : compareObj.previous;
    return source?.[fieldName];
  };

  const isDimensionColumn = (col: IColumnDef, index: number) => {
    const key = getColumnKey(col);
    if (filterDimension && key) {
      return key.toLowerCase() === filterDimension.toLowerCase();
    }
    return index === 0;
  };

  const isTextLikeColumn = (col: IColumnDef) => {
    if (col.type) {
      return col.metricType === 'text';
    }

    const key = getColumnKey(col);
    if (!key) {
      return true;
    }

    const sampleSize = Math.min(5, data.rows.length);
    const sampleValues = data.rows.slice(0, sampleSize).map((row) => getRowValue(row, key));
    const parsed = sampleValues.map((v) => parseValue(v));
    return !parsed.some((p) => p.isNumeric);
  };

  const unsplitColumns = displayColumns.filter((col, index) => isDimensionColumn(col, index) || isTextLikeColumn(col));
  const splitColumns = displayColumns.filter((col, index) => !isDimensionColumn(col, index) && !isTextLikeColumn(col));

  const previousCellClassName = 'bg-[#f5fafa]';
  const previousTextColor = '#2f6f73';

  const isExpandableTable = hasExpandableRows(data);

  const handleRowClick = (rowId: string, event?: React.MouseEvent) => {
    if (!isExpandableTable) return;
    event?.stopPropagation();

    userInteractedWithExpansionRef.current = true;

    setExpandedRows((prev) => {
      if (prev[rowId]) {
        const newExpandedRows = { ...prev };
        delete newExpandedRows[rowId];

        Object.keys(newExpandedRows).forEach((key) => {
          if (key.startsWith(rowId + '-exp-')) {
            delete newExpandedRows[key];
          }
        });

        return newExpandedRows;
      }

      const newExpandedRows = { ...prev };
      newExpandedRows[rowId] = true;
      return newExpandedRows;
    });
  };

  const findMatchingColumn = (
    parentCol: any,
    expansionColumns: any[],
    colIndex: number,
    breakdownBy?: string
  ) => {
    const parentFieldName = parentCol.field || parentCol.metric || '';

    const matchingCol = expansionColumns.find((expCol) => {
      const expFieldName = expCol.field || expCol.metric || '';
      if (colIndex === 0) {
        return expFieldName === parentFieldName || expFieldName === breakdownBy;
      }
      return expFieldName === parentFieldName;
    });

    const fieldName = matchingCol ? (matchingCol.field || matchingCol.metric || '') : colIndex === 0 ? breakdownBy : parentFieldName;

    return { matchingCol, fieldName };
  };

  const calculatePercentageChange = (current: any, previous: any): string | null => {
    const currentNum = typeof current === 'number' ? current : parseFloat(String(current).replace(/[^0-9.-]/g, ''));
    const previousNum = typeof previous === 'number' ? previous : parseFloat(String(previous).replace(/[^0-9.-]/g, ''));

    if (isNaN(currentNum) || isNaN(previousNum) || previousNum === 0) {
      return null;
    }

    const percentChange = ((currentNum - previousNum) / Math.abs(previousNum)) * 100;
    const sign = percentChange > 0 ? '+' : '';
    return `${sign}${percentChange.toFixed(0)}%`;
  };

  const formatCellValue = (value: any, column: any, previousValue?: any, isSummaryRow: boolean = false, row?: Record<string, any>) => {
    const metricName = (column?.field || column?.metric || '') as string;
    const columnHeader = column?.header || '';
    const compareDirection = column?.compareDirection || 'positive';

    // Handle conversion_path column with icons
    if (metricName.toLowerCase() === 'conversion_path' || metricName.toLowerCase() === 'conversionpath') {
      const conversionType = row?.target_event ?? row?.transactionType ?? row?.transaction_type;
      return <ConversionPathCell value={value} transactionType={conversionType} />;
    }

    const columnTypeInfo = getColumnType(column);

    // For null/undefined values, show dash to indicate metric is unavailable
    if (value === null || value === undefined) {
      const numericTypes = ['number', 'currency', 'percentage', 'rating', 'share', 'time'];
      if (numericTypes.includes(columnTypeInfo.type)) {
        return <div className="flex justify-center">-</div>;
      }
      return '';
    }

    // Handle URL type with URLCell component
    if (columnTypeInfo.type === 'url') {
      return <div className="flex justify-center"><URLCell url={value} /></div>;
    }

    // Handle rating type with StarRating component
    if (columnTypeInfo.type === 'rating') {
      return <div className="flex justify-center"><StarRating value={value} /></div>;
    }

    // Handle share type with CircularProgress component
    if (columnTypeInfo.type === 'share') {
      return <div className="flex justify-center"><CircularProgress value={value} /></div>;
    }

    // Handle activity type with ActivityIcon component
    if (columnTypeInfo.type === 'activity') {
      return <ActivityIcon activity={value} />;
    }

    // Handle SERP Features column — comma-delimited list of feature labels
    if (columnTypeInfo.type === 'serp-features') {
      return <SERPFeaturesCell value={value} />;
    }

    // Handle Pages Cited in AIO column — newline-delimited list of cited URLs
    if (columnTypeInfo.type === 'aio-pages') {
      return <AioPagesCell value={value} />;
    }

    // Handle sold columns with green badge (unless it's a summary row)
    const isSoldColumn = metricName === 'sold' || metricName === 'soldAmount' || columnHeader.toLowerCase() === 'sold';
    if (!isSummaryRow && isSoldColumn) {
      const displayValue = value === null || value === undefined ? 0 : value;
      const formattedValue = formatValueByType(displayValue, columnTypeInfo.type);
      return (
        <SoldBadgeWrapper>
          {formattedValue}
        </SoldBadgeWrapper>
      );
    }

    const formattedValue = formatValueByType(value, columnTypeInfo.type);

    // Handle text truncation with tooltip
    if (columnTypeInfo.type === 'text' && typeof formattedValue === 'string' && formattedValue.length > 150) {
      const truncated = truncateText(formattedValue, 150);
      return <span title={formattedValue}>{truncated}</span>;
    }

    // Use Badge component only for leads/trueLeads columns
    if (columnTypeInfo.shouldUseBadge) {
      return (
        <ColumnWrapper metricName={metricName}>
          <div className="flex justify-center">
            <Badge variant="lead" className="mx-auto">
              {formattedValue}
            </Badge>
          </div>
        </ColumnWrapper>
      );
    }

    // Calculate and display percentage change if previousValue is provided
    if (previousValue !== undefined && previousValue !== null) {
      const percentChange = calculatePercentageChange(value, previousValue);
      if (percentChange !== null) {
        const isPositive = percentChange.startsWith('+');
        const isNegative = percentChange.startsWith('-');
        
        let changeColor = '#6b7280';
        if (isPositive) {
          changeColor = compareDirection === 'positive' ? '#16a34a' : '#dc2626';
        } else if (isNegative) {
          changeColor = compareDirection === 'positive' ? '#dc2626' : '#16a34a';
        }
        return (
          <ColumnWrapper metricName={metricName}>
            <div className="flex flex-col items-center">
              <span>{formattedValue}</span>
              <span style={{ fontSize: '0.75rem', color: changeColor, fontWeight: 500 }}>({percentChange})</span>
            </div>
          </ColumnWrapper>
        );
      }
    }

    return <ColumnWrapper metricName={metricName}>{formattedValue}</ColumnWrapper>;
  };

  const formatCellValuePlain = (value: any, column: any, row?: Record<string, any>) => {
    const metricName = (column?.field || column?.metric || '') as string;

    // Handle conversion_path column with icons
    if (metricName.toLowerCase() === 'conversion_path' || metricName.toLowerCase() === 'conversionpath') {
      const conversionType = row?.target_event ?? row?.transactionType ?? row?.transaction_type;
      return <ConversionPathCell value={value} transactionType={conversionType} />;
    }

    const columnTypeInfo = getColumnType(column);

    // For null/undefined values, show dash to indicate metric is unavailable
    if (value === null || value === undefined) {
      const numericTypes = ['number', 'currency', 'percentage', 'rating', 'share', 'time'];
      if (numericTypes.includes(columnTypeInfo.type)) {
        return <div className="flex justify-center">-</div>;
      }
      return '';
    }

    // Handle URL type with URLCell component
    if (columnTypeInfo.type === 'url') {
      return <div className="flex justify-center"><URLCell url={value} /></div>;
    }

    // Handle rating type with StarRating component
    if (columnTypeInfo.type === 'rating') {
      return <div className="flex justify-center"><StarRating value={value} /></div>;
    }

    // Handle share type with CircularProgress component
    if (columnTypeInfo.type === 'share') {
      return <div className="flex justify-center"><CircularProgress value={value} /></div>;
    }

    // Handle activity type with ActivityIcon component
    if (columnTypeInfo.type === 'activity') {
      return <ActivityIcon activity={value} />;
    }

    // Handle SERP Features column — comma-delimited list of feature labels
    if (columnTypeInfo.type === 'serp-features') {
      return <SERPFeaturesCell value={value} />;
    }

    // Handle Pages Cited in AIO column — newline-delimited list of cited URLs
    if (columnTypeInfo.type === 'aio-pages') {
      return <AioPagesCell value={value} />;
    }

    const formattedValue = formatValueByType(value, columnTypeInfo.type);

    if (columnTypeInfo.type === 'text' && typeof formattedValue === 'string' && formattedValue.length > 150) {
      return <ColumnWrapper metricName={metricName}>{truncateText(formattedValue, 150)}</ColumnWrapper>;
    }

    return <ColumnWrapper metricName={metricName}>{formattedValue}</ColumnWrapper>;
  };

  const renderExpansionCell = (
    value: any,
    column: any,
    isFirstColumn: boolean,
    paddingClass: string,
    hasChevron: boolean = false,
    isExpanded: boolean = false
  ) => {
    if (!isFirstColumn) {
      return formatCellValue(value, column);
    }

    return (
      <div className={`flex items-center gap-2 ${paddingClass}`}>
        {hasChevron && (
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        )}
        {formatCellValue(value, column)}
      </div>
    );
  };

  // Check if a row is a total summary row (backend-provided)
  const isSummaryRow = (row: Record<string, any>): boolean => {
    const rowId = (row as any)?.rowId;
    return typeof rowId === 'string' && rowId.startsWith('summary_');
  };

  const handleSort = (field: string) => {
    const newDir = field === sortField && sortDir === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDir(newDir);
    setDisplayedRowsCount(15);
  };

  const sortedRows = (data.rows && Array.isArray(data.rows) ? [...data.rows] : []).sort((a, b) => {
    if (!sortField) return 0;

    // Always keep backend-provided summary rows at the bottom
    const aIsSummary = isSummaryRow(a);
    const bIsSummary = isSummaryRow(b);
    if (aIsSummary && !bIsSummary) return 1;
    if (!aIsSummary && bIsSummary) return -1;
    if (aIsSummary && bIsSummary) return 0;

    try {
      const aVal = getRowValue(a, sortField);
      const bVal = getRowValue(b, sortField);

      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return 1;
      if (bVal === undefined) return -1;

      const aParsed = parseValue(aVal);
      const bParsed = parseValue(bVal);

      if (aParsed.isNumeric && bParsed.isNumeric) {
        return sortDir === 'asc' ? aParsed.numValue - bParsed.numValue : bParsed.numValue - aParsed.numValue;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return sortDir === 'asc' ? (aStr > bStr ? 1 : -1) : aStr < bStr ? 1 : -1;
    } catch (error) {
      console.error(`🚨 [TremorCompareTable] Error sorting by ${sortField}:`, error);
      return 0;
    }
  });

  // Separate summary rows from regular rows
  const summaryRows = sortedRows.filter(row => isSummaryRow(row));
  const regularRows = sortedRows.filter(row => !isSummaryRow(row));
  
  const totalRows = regularRows.length;
  const displayedRegularRows = regularRows.slice(0, displayedRowsCount);
  const hasMoreRows = totalRows > displayedRowsCount;
  
  // Always show summary rows at the bottom, after displayed regular rows
  const displayedRows = [...displayedRegularRows, ...summaryRows];

  const handleLoadMore = () => {
    setDisplayedRowsCount(prev => Math.min(prev + 15, totalRows));
  };

  const handleCollapse = () => {
    setDisplayedRowsCount(15);
  };

  const isFullyLoaded = displayedRowsCount >= totalRows;

  // If no rows to display, show NoDataView instead of empty table. When a dimension/campaign
  // filter is the reason there are zero rows, show the filter-specific message (LAD-454).
  if (sortedRows.length === 0) {
    const filteredEmpty = !!widgetFiltersController && (widgetFiltersController.dimensionFilterActive || widgetFiltersController.selectedDimensionItems.length > 0);
    return filteredEmpty ? <FilteredNoDataView /> : <NoDataView />;
  }

  const computeTotals = (columns: IColumnDef[], rows: Record<string, any>[], totalRowType: "sum" | "average" = "sum") => {
    const totals: Record<string, any> = {};

    columns.forEach((column) => {
      const fieldName = getColumnKey(column);
      const { isPercentage, isRating, isTime, isShare } = getColumnTypeInfo(column);

      const sampleSize = Math.min(5, rows.length);
      const samples = rows.slice(0, sampleSize).map((row) => parseValue(getRowValue(row, fieldName)));
      const hasNumericValues = samples.some((s) => s.isNumeric);

      if (!hasNumericValues) {
        totals[fieldName] = '';
        return;
      }

      if (totalRowType === "average") {
        totals[fieldName] = calculateColumnAverage(rows, fieldName, isPercentage);
      } else {
        totals[fieldName] = calculateColumnSum(
          rows,
          fieldName,
          isPercentage,
          isRating,
          isTime,
          isShare
        );
      }
    });

    const firstFieldName = columns[0]?.field || columns[0]?.metric || '';
    if (firstFieldName) {
      totals[firstFieldName] = totalRowType === "average" ? "Average" : "Total";
    }

    return totals;
  };

  // Backend-provided summary row from the current period's tableResponse.
  // When present, its values are the canonical totals (weighted ratios for
  // calculated metrics). Use these to keep the compare-mode total row in
  // sync with the non-compare TremorTable, which only renders this row.
  const backendSummaryCurrent = (_data?.rows ?? []).find((r: any) => {
    const id = r?.rowId;
    return typeof id === 'string' && id.startsWith('summary_');
  }) as Record<string, any> | undefined;

  // Matching summary entry from the unified compare payload, if any.
  // Carries the previous-period summary values.
  const unifiedSummaryEntry = (compare?.unified?.rows ?? []).find((entry: any) => {
    const id = entry?.identifiers?.rowId;
    return typeof id === 'string' && id.startsWith('summary_');
  }) as { current?: Record<string, any>; previous?: Record<string, any> } | undefined;

  const computeCompareTotals = (columns: IColumnDef[], rows: Record<string, any>[], totalRowType: "sum" | "average" = "sum") => {
    const totalsCurrent: Record<string, number | string> = {};
    const totalsPrevious: Record<string, number | string> = {};

    columns.forEach((column) => {
      const fieldName = column.field || column.metric || '';

      if (!fieldName) {
        totalsCurrent[fieldName] = '';
        totalsPrevious[fieldName] = '';
        return;
      }

      const { isPercentage, isRating, isTime, isShare } = getColumnTypeInfo(column);

      const sampleSize = Math.min(5, rows.length);
      const currentSamples = rows.slice(0, sampleSize).map((row) => parseValue(getCompareValue(row, 'current', fieldName)));
      const prevSamples = rows.slice(0, sampleSize).map((row) => parseValue(getCompareValue(row, 'previous', fieldName)));
      const hasNumericValues = [...currentSamples, ...prevSamples].some((s) => s.isNumeric);

      if (!hasNumericValues) {
        totalsCurrent[fieldName] = '';
        totalsPrevious[fieldName] = '';
        return;
      }

      // Prefer backend-provided summary values when available — they carry
      // the correct weighted ratios for calculated metrics (CTR, CPC, etc.)
      // and match what the non-compare table renders.
      const backendCurrent = backendSummaryCurrent?.[fieldName];
      const backendPrevious = unifiedSummaryEntry?.previous?.[fieldName];

      if (backendCurrent !== undefined && backendCurrent !== null && backendCurrent !== '') {
        totalsCurrent[fieldName] = backendCurrent;
      } else if (totalRowType === "average") {
        totalsCurrent[fieldName] = calculateColumnAverage(rows.map(row => ({ [fieldName]: getCompareValue(row, 'current', fieldName) })), fieldName, isPercentage);
      } else {
        totalsCurrent[fieldName] = calculateColumnSum(
          rows.map(row => ({ [fieldName]: getCompareValue(row, 'current', fieldName) })),
          fieldName,
          isPercentage,
          isRating,
          isTime,
          isShare
        );
      }

      if (backendPrevious !== undefined && backendPrevious !== null && backendPrevious !== '') {
        totalsPrevious[fieldName] = backendPrevious;
      } else if (totalRowType === "average") {
        totalsPrevious[fieldName] = calculateColumnAverage(rows.map(row => ({ [fieldName]: getCompareValue(row, 'previous', fieldName) })), fieldName, isPercentage);
      } else {
        totalsPrevious[fieldName] = calculateColumnSum(
          rows.map(row => ({ [fieldName]: getCompareValue(row, 'previous', fieldName) })),
          fieldName,
          isPercentage,
          isRating,
          isTime,
          isShare
        );
      }
    });

    const firstFieldName = columns[0]?.field || columns[0]?.metric || '';
    if (firstFieldName) {
      const label = totalRowType === "average" ? "Average" : "Total";
      totalsCurrent[firstFieldName] = label;
      totalsPrevious[firstFieldName] = label;
    }

    return { totalsCurrent, totalsPrevious };
  };

  const rowsForTotals = regularRows;

  const anyUnifiedRow = rowsForTotals.some((r) => !!(r as any).__compare);
  const totalRowType = data.metadata?.totalRowType;
  const { totalsCurrent, totalsPrevious } = anyUnifiedRow
    ? computeCompareTotals(displayColumns, rowsForTotals, totalRowType)
    : (() => {
      const totals = computeTotals(displayColumns, rowsForTotals, totalRowType);
      return { totalsCurrent: totals, totalsPrevious: totals };
    })();

  const totalColumnCount = unsplitColumns.length + splitColumns.reduce((acc, col) => {
    const fieldKey = col.field || col.metric || '';
    const isColumnExpanded = compareExpanded[fieldKey] || false;
    return acc + (isColumnExpanded ? 2 : 1);
  }, 0);

  const renderBoldIfElement = (content: any) => {
    if (content === null || content === undefined || content === '') return '';
    if (React.isValidElement(content)) {
      const existingClassName = (content as any).props?.className ?? '';
      return React.cloneElement(content as any, { className: `${existingClassName} font-bold` });
    }
    return content;
  };

  return (
    <Card className="w-full h-full">
      <TableRoot className="overflow-x-auto">
        <Table ref={tableRef} className="border-collapse">
          {!widget?.metadata?.['hide-header'] && <TableHead>
            <TableRow>
              {unsplitColumns.map((col, unsplitIndex) => {
                const key = getColumnKey(col);
                const isFirstUnsplit = unsplitIndex === 0;
                const isLastHeaderCell = splitColumns.length === 0 && unsplitIndex === unsplitColumns.length - 1;

                return (
                  <TableHeaderCell
                    rowSpan={2}
                    key={`unsplit-${key}-${unsplitIndex}`}
                    onClick={() => {
                      if (col.sortable && key) {
                        handleSort(key);
                      }
                    }}
                    className={`${isFirstUnsplit ? 'sticky left-0 bg-[#f9f9f9] z-10 shadow-[inset_-1px_0_0_0_rgba(0,0,0,0.1)]' : 'bg-[#f9f9f9]'} ${isLastHeaderCell ? '!border-r-0' : ''} ${col.sortable ? 'cursor-pointer' : ''}`}
                    style={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                      color: '#232F34',
                      textTransform: 'uppercase',
                      fontWeight: 'bold',
                      textAlign: 'center',
                    }}
                  >
                    <div className="flex items-center justify-center">
                      {col.header}
                      {col.sortable && sortField === key && (
                        <span className="ml-2 text-xs">{sortDir === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </TableHeaderCell>
                );
              })}

              {splitColumns.map((col, colIndex) => {
                const fieldKey = col.field || col.metric || '';
                const isLastGroup = colIndex === splitColumns.length - 1;
                const isColumnExpanded = compareExpanded[fieldKey] || false;
                return (
                  <TableHeaderCell
                    key={`group-${fieldKey}-${colIndex}`}
                    colSpan={isColumnExpanded ? 2 : 1}
                    onClick={() => col.sortable && fieldKey && handleSort(fieldKey)}
                    className={`${isLastGroup ? '!border-r-0' : ''} ${col.sortable ? 'cursor-pointer' : ''} bg-[#f9f9f9]`}
                    style={{
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                      color: '#232F34',
                      textTransform: 'uppercase',
                      fontWeight: 'bold',
                      textAlign: 'center',
                    }}
                  >
                    <div className="flex flex-col items-center justify-center gap-1">
                      <div className="flex items-center gap-2">
                        {col.header}
                        {col.sortable && sortField === fieldKey && (
                          <span className="text-xs">{sortDir === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCompareExpanded(prev => ({ ...prev, [fieldKey]: !isColumnExpanded }));
                        }}
                        className="text-[#5a717a] hover:text-[#2d3a3f] transition-colors"
                        aria-label={isColumnExpanded ? 'Collapse comparison' : 'Expand comparison'}
                      >
                        <svg
                          width="24"
                          height="16"
                          viewBox="0 0 24 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {isColumnExpanded ? (
                            <>
                              <path d="M6 5L9 8L6 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M18 5L15 8L18 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </>
                          ) : (
                            <>
                              <path d="M9 5L6 8L9 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M15 5L18 8L15 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </>
                          )}
                        </svg>
                      </button>
                    </div>
                  </TableHeaderCell>
                );
              })}
            </TableRow>

            {Object.values(compareExpanded).some(v => v) && (
              <TableRow>
                {splitColumns.map((col, colIndex) => {
                  const fieldKey = col.field || col.metric || '';
                  const isColumnExpanded = compareExpanded[fieldKey] || false;
                  const isLastPair = colIndex === splitColumns.length - 1;

                  if (!isColumnExpanded) {
                    return (
                      <TableHeaderCell
                        key={`sub-${getColumnKey(col)}-${colIndex}`}
                        className={`bg-[#f9f9f9] ${isLastPair ? '!border-r-0' : ''}`}
                      />
                    );
                  }

                  return (
                    <React.Fragment key={`sub-${getColumnKey(col)}-${colIndex}`}>
                      <TableHeaderCell
                        className={`bg-[#f9f9f9] ${col.sortable ? 'cursor-pointer' : ''}`}
                        style={{
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: '12px',
                          color: '#232F34',
                          fontWeight: 500,
                          textAlign: 'center',
                        }}
                      >
                        {currentLabel}
                      </TableHeaderCell>
                      <TableHeaderCell
                        className={`bg-[#f9f9f9] ${isLastPair ? '!border-r-0' : ''}`}
                        style={{
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: '12px',
                          color: '#232F34',
                          fontWeight: 500,
                          textAlign: 'center',
                        }}
                      >
                        {compareLabel}
                      </TableHeaderCell>
                    </React.Fragment>
                  );
                })}
              </TableRow>
            )}
          </TableHead>}

          <TableBody>
            {displayedRows.length > 0 ? (
              displayedRows.map((row, rowIndex) => {
                const isTotal = isSummaryRow(row);

                const hasExpansion = isExpandableTable && 'expansion' in row && (row as any).expansion;
                const rowId = isExpandableTable && 'rowId' in row ? (row as any).rowId : `row-${rowIndex}`;
                const isExpanded = !!(hasExpansion && expandedRows[rowId]);
                const expansionData = hasExpansion ? ((row as any).expansion as any) : undefined;

                return (
                  <React.Fragment key={`fragment-${rowId}`}>
                    <TableRow
                      key={`main-${rowId}`}
                      className={`${isTotal ? 'bg-[#ffffff]' : ''} ${hasExpansion ? 'cursor-pointer' : ''} hover:bg-gray-50`}
                      onClick={hasExpansion ? (e) => handleRowClick(rowId, e) : undefined}
                    >
                      {unsplitColumns.map((col, unsplitIndex) => {
                        const key = getColumnKey(col);
                        const isFirstUnsplit = unsplitIndex === 0;
                        const isTextCol = isTextLikeColumn(col);

                        const baseValue = getRowValue(row, key);

                        return (
                          <TableCell
                            key={`unsplit-cell-${key}-${unsplitIndex}`}
                            className={`${isFirstUnsplit ? 'sticky left-0 bg-white z-5 shadow-[inset_-1px_0_0_0_rgba(0,0,0,0.1)]' : ''} ${isTextCol ? 'text-left' : 'text-center'}`}
                            isFirstColumn={isFirstUnsplit}
                            fontWeight={isTotal ? "700" : undefined}
                          >
                            {renderExpansionCell(baseValue, col, isFirstUnsplit, '', hasExpansion && isFirstUnsplit, isExpanded)}
                          </TableCell>
                        );
                      })}

                      {splitColumns.map((column, colIndex) => {
                        const fieldName = column.field || column.metric || '';
                        const isColumnExpanded = compareExpanded[fieldName] || false;
                        const anyUnifiedRow = !!(row as any).__compare;
                        const currentRaw = anyUnifiedRow ? getCompareValue(row, 'current', fieldName) : getRowValue(row, fieldName);
                        const previousRaw = anyUnifiedRow ? getCompareValue(row, 'previous', fieldName) : getRowValue(row, fieldName);
                        const formatted = formatCellValue(currentRaw, column, anyUnifiedRow ? previousRaw : undefined, isTotal, row as Record<string, any>);
                        const previousFormatted = formatCellValuePlain(previousRaw, column, row as Record<string, any>);
                        const isLastPair = colIndex === splitColumns.length - 1;
                        const columnTypeInfo = getColumnType(column);
                        const isTextCol = columnTypeInfo.type === 'text';

                        return (
                          <React.Fragment key={`cell-${fieldName}-${colIndex}`}>
                            <TableCell className={isTextCol ? 'text-left' : 'text-center'} fontWeight={isTotal ? "700" : undefined}>{formatted}</TableCell>
                            {isColumnExpanded && (
                              <TableCell
                                className={`${isLastPair ? 'border-r-0!' : ''} ${isTextCol ? 'text-left' : 'text-center'} ${previousCellClassName}`}
                                fontWeight={isTotal ? "700" : undefined}
                              >
                                <span style={{ color: previousTextColor }}>{previousFormatted}</span>
                              </TableCell>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableRow>

                    {hasExpansion && isExpanded && expansionData &&
                      (expansionData as any).rows.map((expRow: any, expRowIdx: number) => {
                        const hasNestedExpansion = 'expansion' in expRow && (expRow as any).expansion;
                        const nestedRowId = 'rowId' in expRow ? (expRow as any).rowId : `${rowId}-exp-${expRowIdx}`;
                        const isNestedExpanded = !!(hasNestedExpansion && expandedRows[nestedRowId]);
                        const nestedExpansionData = hasNestedExpansion ? ((expRow as any).expansion as any) : undefined;

                        return (
                          <React.Fragment key={`expansion-fragment-${rowId}-${expRowIdx}`}>
                            <TableRow
                              key={`expansion-${rowId}-${expRowIdx}`}
                              className={`bg-gray-50 ${hasNestedExpansion ? 'cursor-pointer' : ''} hover:bg-gray-100`}
                              onClick={hasNestedExpansion ? (e) => handleRowClick(nestedRowId, e) : undefined}
                            >
                              {unsplitColumns.map((parentCol, colIndex) => {
                                const { matchingCol: matchingExpCol, fieldName: expFieldName } = findMatchingColumn(
                                  parentCol,
                                  (expansionData as any).columns,
                                  colIndex,
                                  (expansionData as any).metadata?.breakdownBy
                                );

                                const expValue = getRowValue(expRow, expFieldName ?? '');
                                const isFirstColumn = colIndex === 0;
                                const isTextCol = isTextLikeColumn(parentCol);

                                return (
                                  <TableCell
                                    key={`exp-unsplit-${colIndex}`}
                                    className={`${isFirstColumn ? 'sticky left-0 bg-gray-50 z-5 shadow-[inset_-1px_0_0_0_rgba(0,0,0,0.1)]' : ''} ${isTextCol ? 'text-left' : 'text-center'}`}
                                    isFirstColumn={isFirstColumn}
                                  >
                                    {renderExpansionCell(
                                      expValue,
                                      matchingExpCol || parentCol,
                                      isFirstColumn,
                                      'pl-6',
                                      hasNestedExpansion && isFirstColumn,
                                      isNestedExpanded
                                    )}
                                  </TableCell>
                                );
                              })}

                              {splitColumns.map((parentCol, splitIndex) => {
                                const parentColIndex = unsplitColumns.length + splitIndex;
                                const fieldName = parentCol.field || parentCol.metric || '';
                                const isColumnExpanded = compareExpanded[fieldName] || false;
                                const { matchingCol: matchingExpCol, fieldName: expFieldName } = findMatchingColumn(
                                  parentCol,
                                  (expansionData as any).columns,
                                  parentColIndex,
                                  (expansionData as any).metadata?.breakdownBy
                                );

                                const anyUnifiedRow = !!(expRow as any).__compare;
                                const expField = expFieldName ?? '';
                                const currentRaw = anyUnifiedRow ? getCompareValue(expRow, 'current', expField) : getRowValue(expRow, expField);
                                const previousRaw = anyUnifiedRow ? getCompareValue(expRow, 'previous', expField) : getRowValue(expRow, expField);

                                const formatted = formatCellValue(currentRaw, matchingExpCol || parentCol, anyUnifiedRow ? previousRaw : undefined, false);
                                const previousFormatted = formatCellValuePlain(previousRaw, matchingExpCol || parentCol);
                                const isLastPair = splitIndex === splitColumns.length - 1;
                                const columnTypeInfo = getColumnType(matchingExpCol || parentCol);
                                const isTextCol = columnTypeInfo.type === 'text';

                                return (
                                  <React.Fragment key={`exp-split-${splitIndex}`}>
                                    <TableCell className={isTextCol ? 'text-left' : 'text-center'}>{formatted}</TableCell>
                                    {isColumnExpanded && (
                                      <TableCell
                                        className={`${isLastPair ? 'border-r-0!' : ''} ${isTextCol ? 'text-left' : 'text-center'} ${previousCellClassName}`}
                                      >
                                        <span style={{ color: previousTextColor }}>{previousFormatted}</span>
                                      </TableCell>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </TableRow>

                            {hasNestedExpansion && isNestedExpanded && nestedExpansionData &&
                              (nestedExpansionData as any).rows.map((nestedRow: any, nestedRowIdx: number) => (
                                <TableRow
                                  key={`nested-expansion-${rowId}-${expRowIdx}-${nestedRowIdx}`}
                                  className="bg-gray-100 hover:bg-gray-200"
                                >
                                  {unsplitColumns.map((parentCol, colIndex) => {
                                    const { matchingCol: matchingNestedCol, fieldName: nestedFieldName } = findMatchingColumn(
                                      parentCol,
                                      (nestedExpansionData as any).columns,
                                      colIndex,
                                      (nestedExpansionData as any).metadata?.breakdownBy
                                    );
                                    const nestedValue = getRowValue(nestedRow, nestedFieldName ?? '');
                                    const isFirstColumn = colIndex === 0;
                                    const isTextCol = isTextLikeColumn(parentCol);

                                    return (
                                      <TableCell
                                        key={`nested-unsplit-${colIndex}`}
                                        className={`${isFirstColumn ? 'sticky left-0 bg-gray-100 z-5 shadow-[inset_-1px_0_0_0_rgba(0,0,0,0.1)]' : ''} ${isTextCol ? 'text-left' : 'text-center'}`}
                                        isFirstColumn={isFirstColumn}
                                      >
                                        {renderExpansionCell(
                                          nestedValue,
                                          matchingNestedCol || parentCol,
                                          isFirstColumn,
                                          'pl-12',
                                          false,
                                          false
                                        )}
                                      </TableCell>
                                    );
                                  })}

                                  {splitColumns.map((parentCol, splitIndex) => {
                                    const parentColIndex = unsplitColumns.length + splitIndex;
                                    const fieldName = parentCol.field || parentCol.metric || '';
                                    const isColumnExpanded = compareExpanded[fieldName] || false;
                                    const { matchingCol: matchingNestedCol, fieldName: nestedFieldName } = findMatchingColumn(
                                      parentCol,
                                      (nestedExpansionData as any).columns,
                                      parentColIndex,
                                      (nestedExpansionData as any).metadata?.breakdownBy
                                    );
                                    const anyUnifiedRow = !!(nestedRow as any).__compare;
                                    const nestedField = nestedFieldName ?? '';
                                    const currentRaw = anyUnifiedRow ? getCompareValue(nestedRow, 'current', nestedField) : getRowValue(nestedRow, nestedField);
                                    const previousRaw = anyUnifiedRow ? getCompareValue(nestedRow, 'previous', nestedField) : getRowValue(nestedRow, nestedField);

                                    const formatted = formatCellValue(currentRaw, matchingNestedCol || parentCol, anyUnifiedRow ? previousRaw : undefined, false);
                                    const previousFormatted = formatCellValuePlain(previousRaw, matchingNestedCol || parentCol);
                                    const isLastPair = splitIndex === splitColumns.length - 1;
                                    const columnTypeInfo = getColumnType(matchingNestedCol || parentCol);
                                    const isTextCol = columnTypeInfo.type === 'text';

                                    return (
                                      <React.Fragment key={`nested-split-${splitIndex}`}>
                                        <TableCell className={isTextCol ? 'text-left' : 'text-center'}>{formatted}</TableCell>
                                        {isColumnExpanded && (
                                          <TableCell
                                            className={`${isLastPair ? 'border-r-0!' : ''} ${isTextCol ? 'text-left' : 'text-center'} ${previousCellClassName}`}
                                          >
                                            <span style={{ color: previousTextColor }}>{previousFormatted}</span>
                                          </TableCell>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </TableRow>
                              ))
                            }
                          </React.Fragment>
                        );
                      })
                    }
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={totalColumnCount} className="text-center">
                  No data available
                </TableCell>
              </TableRow>
            )}

            {/* Total Row - only show if there are numeric metrics, not hidden by metadata, and data doesn't already contain total rows */}
            {displayedRows.length > 0 && hasNumericMetrics(data.columns, data.rows) && !data.metadata?.hideTotalRow && summaryRows.length === 0 && (
              <TableRow>
                {unsplitColumns.map((column, unsplitIndex) => {
                  const fieldName = getColumnKey(column);
                  const value = totalsCurrent[fieldName];
                  const formattedValue = value !== '' ? formatCellValue(value, column) : '';
                  const isFirstUnsplit = unsplitIndex === 0;
                  const isLastHeaderCell = splitColumns.length === 0 && unsplitIndex === unsplitColumns.length - 1;
                  const isTextCol = isTextLikeColumn(column);

                  return (
                    <TableCell
                      key={`total-unsplit-${fieldName}-${unsplitIndex}`}
                      fontWeight="700"
                      className={`${isFirstUnsplit ? 'sticky left-0 text-left bg-white z-10 shadow-[inset_-1px_0_0_0_rgba(0,0,0,0.1)]' : ''} ${isTextCol ? 'text-left' : ''} ${isLastHeaderCell ? '!border-r-0' : ''} border-b-0`}
                      isFirstColumn={isFirstUnsplit}
                    >
                      {value === '' ? '' : renderBoldIfElement(formattedValue)}
                    </TableCell>
                  );
                })}

                {splitColumns.map((column, metricIndex) => {
                  const fieldName = getColumnKey(column);
                  const isColumnExpanded = compareExpanded[fieldName] || false;
                  const valueCurrent = totalsCurrent[fieldName];
                  const valuePrevious = totalsPrevious[fieldName];
                  const formattedValue = valueCurrent !== '' ? formatCellValue(valueCurrent, column, valuePrevious !== '' ? valuePrevious : undefined) : '';
                  const formattedValuePrevious = valuePrevious !== '' ? formatCellValuePlain(valuePrevious, column) : '';
                  const isLastPair = metricIndex === splitColumns.length - 1;

                  return (
                    <React.Fragment key={`total-metric-${fieldName}-${metricIndex}`}>
                      <TableCell fontWeight="700" className="border-b-0 text-center">
                        {valueCurrent === '' ? '' : renderBoldIfElement(formattedValue)}
                      </TableCell>
                      {isColumnExpanded && (
                        <TableCell
                          fontWeight="700"
                          className={`${isLastPair ? '!border-r-0' : ''} border-b-0 text-center ${previousCellClassName}`}
                        >
                          {valuePrevious === '' ? '' : <span style={{ color: previousTextColor }}>{formattedValuePrevious}</span>}
                        </TableCell>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableRoot>

      {/* Load More Controls */}
      {totalRows > 15 && (
        <div className="relative flex items-center mt-4 pt-4">
          <div className="text-sm text-gray-500">
            Showing 1-{displayedRowsCount} of {totalRows}
          </div>
          <div className="absolute left-1/2 transform -translate-x-1/2 flex gap-2">
            <Button
              variant="outline"
              className="px-6 py-2.5"
              onClick={handleLoadMore}
              disabled={isFullyLoaded}
            >
              Load More
            </Button>
            {displayedRowsCount > 15 && (
              <Button
                variant="outline"
                className="px-6 py-2.5"
                onClick={handleCollapse}
              >
                Collapse to Default
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
